import styled, { css } from 'styled-components';

import { ThemeMode } from '@/helpers/enums';

import { device } from '@/styles/helpers';

export interface ScSelectDepositProps {
  active: boolean;
  isInputAddon?: boolean;
  withoutSum?: boolean;
  wide?: boolean;
  transparent?: boolean;
  boldValue?: boolean;
  blackBg?: boolean;
  mode?: ThemeMode;
  minWidth?: string;
  inset?: string;
  reducedSizeOnSmallScreen?: boolean;
}

export const ScSelectDeposit = styled.div<ScSelectDepositProps>`
  position: relative;
  width: ${({ wide }) => (wide ? '100%' : 'max-content')};

  @media (${device.mobileS}) {
    width: auto;
  }

  & > .select-button {
    position: relative;
    z-index: 12;
    background-color: ${({ theme, blackBg }) =>
      blackBg ? theme.blackColor : theme.grayLightColor};
    display: flex;
    gap: 8px;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-items: center;
    height: 40px;
    width: 100%;
    min-width: 40px;
    border-width: 1px;
    border-style: solid;
    border-color: ${({ active, transparent, theme }) => {
      return active
        ? transparent
          ? `transparent`
          : theme.whiteColor
        : 'transparent';
    }};
    border-radius: 28px;
    font-size: 16px;
    line-height: 24px;
    color: ${({ theme }) => theme.blackColor};
    transition: border-color 0.2s;
    cursor: pointer;

    &:hover {
      border: 1px solid
        ${({ theme, transparent }) =>
          transparent ? 'transparent' : theme.whiteColor};
      & > .arrow {
        svg {
          path {
            stroke: ${({ theme }) => theme.whiteColor};
          }
        }
      }
    }

    & > .value {
      font-size: 16px;
      line-height: 24px;
      color: ${({ theme }) => theme.whiteColor};
      display: flex;
      align-items: center;
      font-weight: ${({ boldValue }) => (boldValue ? 700 : 400)};
      padding-left: 16px;

      .amount {
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;

        @media (${device.mobileS}) {
          max-width: 75px;
        }
      }
    }

    & > .arrow {
      width: 16px;
      display: flex;
      justify-content: center;
      transition: transform 0.2s;
      transform: ${({ active }) =>
        active ? 'rotate(180deg)' : 'rotate(0deg)'};

      svg {
        display: block;
        path {
          stroke: ${({ theme, active }) =>
            active ? theme.whiteColor : theme.blackLightColor};
        }
      }
    }
  }

  & > .options {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    right: 0;
    z-index: 16;
    background-color: ${({ theme }) => theme.grayLightColor};
    padding: 8px 0;
    border-radius: 16px;
    overflow: hidden;
    min-width: 128px;
    box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.9);

    ul {
      display: block;
      list-style: none;

      & > li {
        padding: 8px 16px;
        cursor: pointer;
        background-color: transparent;
        transition: color 0.2s;
        font-size: 16px;
        line-height: 24px;
        color: ${({ theme }) => theme.grayColor};

        &:hover {
          color: ${({ theme }) => theme.whiteColor};
        }
      }
    }
  }

  ${({
    isInputAddon,
    mode,
    theme,
    active,
    inset,
    wide,
    minWidth,
    blackBg,
    reducedSizeOnSmallScreen,
  }) =>
    isInputAddon &&
    css`
      & > .select-button {
        background-color: ${blackBg
          ? theme.blackColor
          : mode === ThemeMode.Dark
            ? theme.grayDarkColor
            : theme.whiteColor};
        height: 32px;
        width: 100%;
        min-width: 80px;
        border-radius: 28px;
        border: none;
        transition: none;
        padding: 0 12px;
        gap: 8px;

        &:hover {
          border: none;
          & > .arrow {
            svg {
              path {
                stroke: ${mode === ThemeMode.Dark
                  ? theme.whiteColor
                  : theme.blackColor};
              }
            }
          }
        }

        &:disabled {
          cursor: not-allowed;

          & > * {
            opacity: 0.4;
          }
        }

        & > .value {
          color: ${mode === ThemeMode.Dark
            ? theme.whiteColor
            : theme.blackColor};
          font-weight: 700;
          padding: 0;

          .amount {
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;

            @media (${device.mobileS}) {
              max-width: 75px;
            }
          }

          ${reducedSizeOnSmallScreen &&
          css`
            @media (max-width: 360px) {
              font-size: 14px;
              line-height: 15px;
            }
          `}
        }

        & > .arrow {
          svg {
            path {
              stroke: ${active
                ? mode === ThemeMode.Dark
                  ? theme.whiteColor
                  : theme.blackColor
                : theme.blackLightColor};
            }
          }
        }
      }

      & > .options {
        position: absolute;
        inset: ${inset ?? 'calc(100% + 4px) 0 auto auto'};
        background-color: ${mode === ThemeMode.Dark
          ? theme.grayDarkColor
          : theme.whiteColor};
        padding: 16px;
        border-radius: 24px;
        width: ${wide ? '100%' : 'max-content'};
        box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.3);
        display: flex;
        flex-direction: column;
        gap: 16px;

        ${minWidth &&
        css`
          min-width: ${minWidth};
        `}

        ul {
          display: flex;
          flex-direction: column;
          gap: 16px;

          & > li {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
            padding: 0;
            color: ${({ theme }) => theme.blackLightColor};

            &.active {
              color: ${mode === ThemeMode.Dark
                ? theme.whiteColor
                : theme.blackColor};
            }

            svg {
              width: 24px;
              height: 24px;
              object-fit: contain;
            }

            &:hover {
              color: ${mode === ThemeMode.Dark
                ? theme.whiteColor
                : theme.blackColor};
            }
          }
        }
      }
    `}
`;

export const ScOptionsWalletsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;

  .list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
`;
